import { useState, useRef, useEffect } from "react";

export default function WebSocketTest() {
  const [wsStatus, setWsStatus] = useState("disconnected");
  const [inputMessage, setInputMessage] = useState("Hello WebSocket!");
  const [appToWsLog, setAppToWsLog] = useState("");
  const [wsToAppLog, setWsToAppLog] = useState("");
  const [serverVerifyLog, setServerVerifyLog] = useState("");
  const wsRef = useRef(null);

  const getWsUrl = () => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/webSocketTest`;
  };

  const connect = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    setWsStatus("connecting");
    setWsToAppLog("Connecting to " + getWsUrl() + " ...");
    const ws = new WebSocket(getWsUrl());

    ws.onopen = () => {
      setWsStatus("connected");
      setWsToAppLog("Connected to WebSocket server at " + getWsUrl());
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setWsToAppLog(
          `WebSocket → App echo received:\n  Message : "${data.message}"\n  Time    : ${data.timestamp}`,
        );
      } catch {
        setWsToAppLog("WebSocket → App raw message: " + event.data);
      }
    };

    ws.onclose = () => {
      setWsStatus("disconnected");
      setWsToAppLog((prev) => prev + "\nDisconnected from WebSocket server.");
    };

    ws.onerror = () => {
      setWsStatus("error");
      setWsToAppLog((prev) => prev + "\nWebSocket connection error.");
    };

    wsRef.current = ws;
  };

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const sendToWebSocket = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setAppToWsLog('Not connected. Please click "Connect" first.');
      return;
    }
    const ts = new Date().toISOString();
    wsRef.current.send(inputMessage);
    setAppToWsLog(
      `App → WebSocket sent:\n  Message : "${inputMessage}"\n  Time    : ${ts}`,
    );
  };

  const verifyServerReceived = async () => {
    setServerVerifyLog("Calling /applicationTest ...");
    try {
      const res = await fetch("/applicationTest");
      const data = await res.json();
      if (data.status === "received") {
        setServerVerifyLog(
          `Server confirmed receipt:\n  Message : "${data.message}"\n  Time    : ${data.timestamp}`,
        );
      } else {
        setServerVerifyLog("Server says: " + data.message);
      }
    } catch (e) {
      setServerVerifyLog("Error reaching /applicationTest: " + e.message);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  const statusTagClass =
    wsStatus === "connected"
      ? "tag is-success"
      : wsStatus === "connecting"
        ? "tag is-warning"
        : wsStatus === "error"
          ? "tag is-danger"
          : "tag is-light";

  return (
    <div
      className="ws-test-section"
      style={{
        margin: "20px 0",
        padding: "20px",
        border: "2px solid #3273dc",
        borderRadius: "6px",
        backgroundColor: "#f8f9fa",
      }}
    >
      <h3 className="title is-5" style={{ marginBottom: "16px" }}>
        WebSocket Two-Way Communication Test
      </h3>

      {/* Connection Status */}
      <div style={{ marginBottom: "12px" }}>
        <span
          className={statusTagClass}
          style={{ fontSize: "14px", padding: "4px 10px" }}
        >
          Status: {wsStatus}
        </span>
      </div>

      {/* Connect / Disconnect */}
      <div style={{ marginBottom: "16px", display: "flex", gap: "8px" }}>
        <button
          className="button is-primary"
          onClick={connect}
          disabled={wsStatus === "connected" || wsStatus === "connecting"}
        >
          Connect to WebSocket
        </button>
        <button
          className="button is-danger"
          onClick={disconnect}
          disabled={wsStatus === "disconnected"}
        >
          Disconnect
        </button>
      </div>

      {/* Message input + Send */}
      <div style={{ marginBottom: "16px" }}>
        <label className="label">Message to send (App → WebSocket):</label>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <input
            className="input"
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Type your message here..."
            style={{ maxWidth: "420px" }}
            onKeyDown={(e) => e.key === "Enter" && sendToWebSocket()}
          />
          <button
            className="button is-info"
            onClick={sendToWebSocket}
            disabled={wsStatus !== "connected"}
          >
            Send to WebSocket
          </button>
        </div>
      </div>

      {/* App → WebSocket log */}
      <div style={{ marginBottom: "16px" }}>
        <label className="label">① App → WebSocket (what was sent):</label>
        <textarea
          className="textarea"
          readOnly
          value={appToWsLog}
          rows={3}
          placeholder="Sent message details will appear here..."
          style={{ fontFamily: "monospace", fontSize: "13px" }}
        />
      </div>

      {/* WebSocket → App log */}
      <div style={{ marginBottom: "16px" }}>
        <label className="label">
          ② WebSocket → App (echo received by app):
        </label>
        <textarea
          className="textarea"
          readOnly
          value={wsToAppLog}
          rows={3}
          placeholder="Echo from WebSocket server will appear here..."
          style={{ fontFamily: "monospace", fontSize: "13px" }}
        />
      </div>

      {/* Server verification */}
      <div style={{ marginBottom: "8px" }}>
        <label className="label">
          ③ Server Verification — confirm WebSocket received the message
          (/applicationTest):
        </label>
        <div style={{ marginBottom: "8px" }}>
          <button className="button is-warning" onClick={verifyServerReceived}>
            Verify Server Received
          </button>
        </div>
        <textarea
          className="textarea"
          readOnly
          value={serverVerifyLog}
          rows={3}
          placeholder="Server verification result will appear here..."
          style={{ fontFamily: "monospace", fontSize: "13px" }}
        />
      </div>

      {/* How to use */}
      <div style={{ marginTop: "16px", fontSize: "12px", color: "#666" }}>
        <strong>How to test:</strong>
        <ol style={{ marginLeft: "20px", marginTop: "4px" }}>
          <li>
            Click <em>Connect to WebSocket</em> — establishes the WS connection.
          </li>
          <li>
            Type a message and click <em>Send to WebSocket</em> — sends data
            from the app to the server (① App → WS).
          </li>
          <li>The server echoes it back immediately (② WS → App).</li>
          <li>
            Click <em>Verify Server Received</em> to call{" "}
            <code>/applicationTest</code> and confirm the server stored the
            message (③ server-side verification).
          </li>
        </ol>
      </div>
    </div>
  );
}
